// src/utils/api-services.js
import QRCode from "qrcode";
import { supabase } from "./supabase";
import { jsPDF } from "jspdf";

/* ====== PLANTILLA (posiciones) ====== */
const AFIP_CALINS = {
  fontSize: 11,
  fontSizeNumber: 13, // tamaño especial solo para el N° de remito
  offsetX: 0,
  offsetY: 0,

  // Encabezado AFIP Calins
  numberCopy: { x: 165, y: 42 }, // N° remito oficial (arriba, a la derecha)
  dateMain: { x: 140, y: 28 }, // Fecha (debajo, más cerca de la palabra "Fecha:")

  // Bloque de cliente
  clientBlockX: 20,
  clientNameY: 70,
  clientAddrY: 77,
  clientLocalY: 84,
  clientProvY: 91,
  clientCUITY: 98,

  clientNumber: { x: 150, y: 77 }, // "Cliente Nº"
  costCenter: { x: 150, y: 84 }, // "C. Costos" (alineado con Localidad)

  tableHeader: { y: 115, xCod: 22, xQty: 55, xDesc: 70 },
  lineH: 6,
  footerY: 233,
};

/* ====== HELPERS ====== */
function dataUrlToBlob(dataUrl) {
  const [meta, base64] = dataUrl.split(",");
  const mime = meta.match(/data:(.*);base64/)?.[1] || "image/png";
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: mime });
}
const mm = (n) => (Number.isFinite(n) ? n : 0);

async function resolveOSIdViaRPC(anyId) {
  if (!anyId) return null;
  try {
    const { data, error } = await supabase.rpc("resolve_service_order_id", {
      p_any_id: anyId,
    });
    if (error) throw error;
    return data ?? anyId; // fallback si el RPC devuelve null
  } catch (e) {
    console.warn(
      "[api] resolveOSIdViaRPC: fallback directo al id. Motivo:",
      e?.message || e
    );
    return anyId;
  }
}

function onlyDigits(str) {
  if (str === 0) return "0";
  if (!str && str !== 0) return null;
  const m = String(str).match(/\d+/g);
  return m ? m.join("") : null;
}

// Extrae centro de costo desde código OS tipo "OS-5927-001"
function extractCostCenterFromOSCode(code) {
  if (!code) return null;
  const m = String(code).match(/OS-(\d{4})-\d+/);
  if (m) return m[1];
  return null;
}

/**
 * Formatea una fecha "date-only" (o timestamptz) a DD/MM/AAAA
 * evitando el corrimiento de día por timezone.
 */
function formatDateDDMMYYYY(value) {
  if (!value) return "";
  // Tomamos solo YYYY-MM-DD si viene como ISO
  const s = String(value);
  const yyyyMmDd = s.includes("T") ? s.slice(0, 10) : s;

  // Creamos una fecha al mediodía para evitar que caiga "día anterior"
  // en zonas horarias negativas/positivas.
  const d = new Date(`${yyyyMmDd}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    // fallback best-effort
    try {
      return new Date(value).toLocaleDateString("es-AR");
    } catch {
      return "";
    }
  }
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function toDateOnly(value) {
  if (!value) return null;
  const s = String(value).trim();
  if (!s) return null;
  return s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
}

async function syncInstrumentDepartureDate(instrumentId) {
  if (!instrumentId) return null;

  const { data: itemRows, error: itemErr } = await supabase
    .from("remito_items")
    .select("remito_id")
    .eq("instrument_id", instrumentId);
  if (itemErr) throw itemErr;

  const remitoIds = Array.from(
    new Set((itemRows || []).map((row) => row?.remito_id).filter(Boolean))
  );

  if (!remitoIds.length) {
    const { error: updErr } = await supabase
      .from("instruments")
      .update({ departure_date: null })
      .eq("id", instrumentId);
    if (updErr) throw updErr;
    return null;
  }

  const { data: remitos, error: remErr } = await supabase
    .from("remitos")
    .select("id, date_out")
    .in("id", remitoIds);
  if (remErr) throw remErr;

  let latestDate = null;
  let latestMs = -Infinity;

  for (const row of remitos || []) {
    const ymd = toDateOnly(row?.date_out);
    if (!ymd) continue;
    const ms = new Date(`${ymd}T12:00:00`).getTime();
    if (Number.isNaN(ms)) continue;
    if (ms >= latestMs) {
      latestMs = ms;
      latestDate = ymd;
    }
  }

  const { error: updErr } = await supabase
    .from("instruments")
    .update({ departure_date: latestDate })
    .eq("id", instrumentId);
  if (updErr) throw updErr;

  return latestDate;
}

async function markOfferReadyToInvoiceByOS(osId, dateOut) {
  if (!osId) return null;

  const { data: osRow, error: osErr } = await supabase
    .from("service_orders")
    .select("id, po_id")
    .eq("id", osId)
    .maybeSingle();
  if (osErr) throw osErr;
  if (!osRow?.po_id) return null;

  const { data: poRow, error: poErr } = await supabase
    .from("purchase_orders")
    .select("id, offer_id")
    .eq("id", osRow.po_id)
    .maybeSingle();
  if (poErr) throw poErr;
  if (!poRow?.offer_id) return null;

  const { data: offerRow, error: offerErr } = await supabase
    .from("offers")
    .select("id, status, billing_requested_date")
    .eq("id", poRow.offer_id)
    .maybeSingle();
  if (offerErr) throw offerErr;
  if (!offerRow?.id) return null;

  if (offerRow.status === "closed" || offerRow.status === "rejected") {
    return offerRow;
  }

  const billingRequestedDate =
    toDateOnly(dateOut) || offerRow.billing_requested_date || new Date().toISOString().slice(0, 10);

  const patch = {
    status: "billing",
  };

  if (!offerRow.billing_requested_date) {
    patch.billing_requested_date = billingRequestedDate;
  }

  const { error: updErr } = await supabase.from("offers").update(patch).eq("id", offerRow.id);
  if (updErr) throw updErr;

  return {
    id: offerRow.id,
    status: "billing",
    billing_requested_date: patch.billing_requested_date || offerRow.billing_requested_date || null,
  };
}

export async function syncDepartureDateForRemito(remito_id) {
  if (!remito_id) return [];

  const { data: items, error } = await supabase
    .from("remito_items")
    .select("instrument_id")
    .eq("remito_id", remito_id);
  if (error) throw error;

  const instrumentIds = Array.from(
    new Set((items || []).map((row) => row?.instrument_id).filter(Boolean))
  );

  return Promise.allSettled(
    instrumentIds.map((instrumentId) => syncInstrumentDepartureDate(instrumentId))
  );
}

/* ====== QR ====== */
export async function generateAndStoreInstrumentQR(instrument, opts = {}) {
  const bucket = opts.bucket ?? "labels";
  const pathPrefix = opts.pathPrefix ?? "qr";
  if (!instrument?.id || !instrument?.qr_payload)
    throw new Error("Instrumento inválido");

  const dataUrl = await QRCode.toDataURL(instrument.qr_payload, {
    errorCorrectionLevel: "M",
    scale: 6,
    margin: 1,
  });
  const blob = dataUrlToBlob(dataUrl);
  const filePath = `${pathPrefix}/${instrument.id}.png`;

  const { error: uploadErr } = await supabase.storage
    .from(bucket)
    .upload(filePath, blob, { upsert: true, contentType: "image/png" });
  if (uploadErr) throw uploadErr;

  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(filePath);
  const publicUrl = pub?.publicUrl;

  const { data: authData } = await supabase.auth.getUser();
  const { error: updErr } = await supabase
    .from("instruments")
    .update({
      qr_image_url: publicUrl,
      qr_generated_at: new Date().toISOString(),
      qr_generated_by: authData?.user?.id || null,
    })
    .eq("id", instrument.id);
  if (updErr) throw updErr;
  return publicUrl;
}

/* ====== REMITOS ====== */
export async function createRemito({
  os_id,
  os,
  code,
  date_out,
  client_id,
  notes,
  official_number,
}) {
  const candidate = os?.id ?? os_id ?? null;
  const resolvedOS = await resolveOSIdViaRPC(candidate);
  if (!resolvedOS) throw new Error("No se pudo resolver la Orden de Servicio.");
  if (!client_id) throw new Error("Falta client_id.");

  const row = {
    os_id: resolvedOS,
    code,
    date_out,
    client_id,
    notes: notes ?? null,
    status: "borrador",
    ...(official_number?.trim()
      ? { official_number: official_number.trim() }
      : {}),
  };
  const { data, error } = await supabase
    .from("remitos")
    .insert(row)
    .select("id")
    .single();
  if (error) throw error;

  try {
    await markOfferReadyToInvoiceByOS(resolvedOS, date_out);
  } catch (markErr) {
    console.warn("[api] markOfferReadyToInvoiceByOS:", markErr?.message || markErr);
  }

  return data.id;
}

export async function addRemitoItem({
  remito_id,
  instrument_id,
  qty = 1,
  state = "OK",
  tag = null,
  note = null,
}) {
  const payload = { remito_id, instrument_id, qty, state, tag, note };
  const { error } = await supabase.from("remito_items").insert(payload);
  if (error) throw error;

  try {
    await syncInstrumentDepartureDate(instrument_id);
  } catch (syncErr) {
    console.warn("[api] sync departure date after addRemitoItem:", syncErr?.message || syncErr);
  }
}

export async function removeRemitoItem(item_id) {
  const { data: item, error: itemErr } = await supabase
    .from("remito_items")
    .select("instrument_id")
    .eq("id", item_id)
    .maybeSingle();
  if (itemErr) throw itemErr;

  const { error } = await supabase.from("remito_items").delete().eq("id", item_id);
  if (error) throw error;

  if (item?.instrument_id) {
    try {
      await syncInstrumentDepartureDate(item.instrument_id);
    } catch (syncErr) {
      console.warn("[api] sync departure date after removeRemitoItem:", syncErr?.message || syncErr);
    }
  }
}

/**
 * ✅ Borra un remito REALMENTE en BD:
 * 1) borra remito_items
 * 2) borra remitos
 */
export async function deleteRemito(remito_id) {
  if (!remito_id) throw new Error("Falta remito_id");

  const { data: remitoItems, error: itemsErr } = await supabase
    .from("remito_items")
    .select("instrument_id")
    .eq("remito_id", remito_id);
  if (itemsErr) throw itemsErr;

  const { error: delItemsErr } = await supabase
    .from("remito_items")
    .delete()
    .eq("remito_id", remito_id);
  if (delItemsErr) throw delItemsErr;

  const { error: delRemErr } = await supabase.from("remitos").delete().eq("id", remito_id);
  if (delRemErr) throw delRemErr;

  const instrumentIds = Array.from(
    new Set((remitoItems || []).map((row) => row?.instrument_id).filter(Boolean))
  );
  await Promise.allSettled(
    instrumentIds.map((instrumentId) => syncInstrumentDepartureDate(instrumentId))
  );

  return true;
}

export async function listRemitosByOS(os_id, osObj = null) {
  const candidate = osObj?.id ?? os_id ?? null;
  if (!candidate) return [];

  // Una PO puede tener una service_order por cada instrumento. Los remitos
  // históricos pueden estar vinculados a cualquiera de ellas, por lo que no
  // debemos depender de resolve_service_order_id (que devuelve solo una).
  let poId = osObj?.po_id ?? null;
  let serviceOrderIds = [];

  if (!poId) {
    const { data: rowsByPO, error: rowsByPOError } = await supabase
      .from("service_orders")
      .select("id")
      .eq("po_id", candidate);

    if (!rowsByPOError && rowsByPO?.length) {
      serviceOrderIds = rowsByPO.map((row) => row.id).filter(Boolean);
    } else {
      if (rowsByPOError) {
        console.warn(
          "[api] listRemitosByOS: no se pudo buscar por PO:",
          rowsByPOError.message
        );
      }

      // El llamador también puede enviar directamente el id de una OS.
      const { data: serviceOrder, error: serviceOrderError } = await supabase
        .from("service_orders")
        .select("id, po_id")
        .eq("id", candidate)
        .maybeSingle();

      if (!serviceOrderError && serviceOrder?.po_id) {
        poId = serviceOrder.po_id;
      } else if (!serviceOrderError && serviceOrder?.id) {
        serviceOrderIds = [serviceOrder.id];
      } else if (serviceOrderError) {
        console.warn(
          "[api] listRemitosByOS: no se pudo identificar la OS:",
          serviceOrderError.message
        );
      }
    }
  }

  if (poId && !serviceOrderIds.length) {
    const { data: siblingOrders, error: siblingOrdersError } = await supabase
      .from("service_orders")
      .select("id")
      .eq("po_id", poId);

    if (!siblingOrdersError) {
      serviceOrderIds = (siblingOrders || []).map((row) => row.id).filter(Boolean);
    } else {
      console.warn(
        "[api] listRemitosByOS: no se pudieron cargar las OS de la PO:",
        siblingOrdersError.message
      );
    }
  }

  // Compatibilidad con órdenes antiguas o sesiones sin acceso a service_orders.
  if (!serviceOrderIds.length) {
    const resolvedOS = await resolveOSIdViaRPC(candidate);
    if (!resolvedOS) return [];
    serviceOrderIds = [resolvedOS];
  }

  const uniqueServiceOrderIds = [...new Set(serviceOrderIds)];
  let query = supabase.from("remitos").select("*");
  query =
    uniqueServiceOrderIds.length === 1
      ? query.eq("os_id", uniqueServiceOrderIds[0])
      : query.in("os_id", uniqueServiceOrderIds);

  const { data, error } = await query
    .order("date_out", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/* ====== ÍTEMS DEL REMITO ====== */
async function listRemitoItemsFromTables(remito_id) {
  console.log("[DEBUG] Intentando cargar items desde tablas para remito:", remito_id);

  const { data: head, error: hErr } = await supabase
    .from("remitos")
    .select("id, os_id, client_id, date_out, official_number")
    .eq("id", remito_id)
    .single();
  if (hErr) throw hErr;

  const { data: items, error: iErr } = await supabase
    .from("remito_items")
    .select("id, instrument_id, qty, tag, note")
    .eq("remito_id", remito_id);
  if (iErr) throw iErr;
  if (!items?.length) return [];

  const instrumentIds = items.map((x) => x.instrument_id);

  // ✅ Traemos categoría (instrument_categories.name)
  const { data: insts, error: insErr } = await supabase
    .from("instruments")
    .select(
      `
      id,
      tag,
      brand,
      model,
      serial,
      type,
      lab_id,
      baker_code,
      instrument_categories(name)
    `
    )
    .in("id", instrumentIds);
  if (insErr) throw insErr;

  const { data: osRow, error: osErr } = await supabase
    .from("service_orders")
    .select("id, code, base_number, po_id")
    .eq("id", head.os_id)
    .single();
  if (osErr) throw osErr;

  const { data: osItems } = await supabase
    .from("service_orders")
    .select("id, instrument_id, code, base_number, po_id")
    .in("instrument_id", instrumentIds);

  const osItemByInstr = new Map();
  for (const osi of osItems || []) {
    if (
      osi.instrument_id &&
      (osi.po_id === osRow.po_id || osi.base_number === osRow.base_number)
    ) {
      osItemByInstr.set(osi.instrument_id, osi);
    }
  }

  const { data: client } = await supabase
    .from("clients")
    .select("id, name, address, locality, province, cuit, tax_id, client_number")
    .eq("id", head.client_id)
    .maybeSingle();

  // Centro de costo desde OS.code o base_number
  let cost_center = extractCostCenterFromOSCode(osRow?.code) ?? null;
  if (!cost_center && osRow?.base_number) {
    cost_center = String(osRow.base_number).padStart(4, "0").slice(-4);
  }

  const findInst = (id) => (insts || []).find((z) => z.id === id) || {};

  // Compongo estructura final con enriquecimiento
  const result = items.map((ri) => {
    const inst = findInst(ri.instrument_id);
    const osi = osItemByInstr.get(ri.instrument_id) || {};

    const catObj = inst?.instrument_categories;
    const categoryName =
      (catObj && !Array.isArray(catObj) ? catObj?.name : catObj?.[0]?.name) || null;

    const labelType = categoryName || (inst.type ? String(inst.type).toUpperCase() : null);

    const instr_label =
      [
        inst.baker_code ? `PO ${inst.baker_code}` : null,
        labelType,
        inst.brand ? `MARCA: ${String(inst.brand).toUpperCase()}` : null,
        inst.model ? `MOD: ${inst.model}` : null,
        inst.serial ? `S/N: ${inst.serial}` : null,
        ri.tag || inst.tag ? `TAG: ${ri.tag || inst.tag}` : null,
      ]
        .filter(Boolean)
        .join(", ") || null;

    return {
      remito_id: head.id,
      id: head.id,
      remito_code: null,
      date_out: head.date_out,
      official_number: head.official_number,
      client_id: head.client_id,
      client_name: client?.name || "",
      client_address: client?.address || "",
      client_locality: client?.locality || "",
      client_province: client?.province || "",
      client_cuit: client?.cuit || client?.tax_id || "",
      client_number: client?.client_number || "",
      cost_center,

      item_id: ri.id,
      qty: ri.qty,
      tag: ri.tag || inst.tag || null,
      note: ri.note || null,

      instrument_id: ri.instrument_id,
      instr_tag: inst.tag,
      instr_brand: inst.brand,
      instr_model: inst.model,
      instr_serial: inst.serial,
      instr_type: inst.type,
      instr_category_name: categoryName, // ✅ NUEVO
      instr_lab_id: inst.lab_id,
      instr_lab_id_text: inst.lab_id ? String(inst.lab_id).padStart(7, "0") : null,
      instr_baker_code: inst.baker_code || null,
      baker_code: inst.baker_code || null,
      instr_label,

      os_code: osRow.code,
      os_base: osRow.base_number,
      os_po_id: osRow.po_id,

      instr_os_code: osi.code || null,
    };
  });

  return result;
}

// Carga desde vista (si existe)
async function listRemitoItemsFromView(remito_id) {
  console.log("[DEBUG] Intentando cargar desde vista v3 para remito:", remito_id);

  const { data: byRemitoId, error: err1 } = await supabase
    .from("v_remitos_print_afip_v3")
    .select("*")
    .eq("remito_id", remito_id)
    .order("item_id", { ascending: true });

  if (!err1 && byRemitoId && byRemitoId.length > 0) return byRemitoId;

  const { data: byId, error: err2 } = await supabase
    .from("v_remitos_print_afip_v3")
    .select("*")
    .eq("id", remito_id)
    .order("item_id", { ascending: true });

  if (!err2 && byId && byId.length > 0) return byId;

  return [];
}

// Con reintento breve
export async function listRemitoItems(remito_id) {
  console.log("[DEBUG] ==== listRemitoItems:", remito_id);

  const attempt = async () => {
    try {
      const fromTables = await listRemitoItemsFromTables(remito_id);
      if (fromTables?.length) return fromTables;
    } catch (e) {
      console.warn("[DEBUG] Tablas fallaron:", e?.message || e);
    }
    try {
      const fromView = await listRemitoItemsFromView(remito_id);
      if (fromView?.length) return fromView;
    } catch (e) {
      console.warn("[DEBUG] Vista falló:", e?.message || e);
    }
    return [];
  };

  let items = await attempt();

  if (!items.length) {
    await new Promise((r) => setTimeout(r, 180));
    items = await attempt();
  }

  // ✅ Enriquecimiento opcional si vienen desde vista sin categoría
  try {
    const need = (items || []).filter(
      (it) => it?.instrument_id && !it?.instr_category_name
    );
    if (need.length) {
      const ids = [...new Set(need.map((x) => x.instrument_id))];
      const { data: insts } = await supabase
        .from("instruments")
        .select(`id, instrument_categories(name)`)
        .in("id", ids);

      const catById = new Map();
      for (const inst of insts || []) {
        const catObj = inst?.instrument_categories;
        const name =
          (catObj && !Array.isArray(catObj) ? catObj?.name : catObj?.[0]?.name) || null;
        catById.set(inst.id, name);
      }

      items = (items || []).map((it) => ({
        ...it,
        instr_category_name: it?.instr_category_name ?? catById.get(it?.instrument_id) ?? null,
      }));
    }
  } catch (e) {
    console.warn("[DEBUG] Enriquecimiento de categoría falló:", e?.message || e);
  }

  if (!items.length) {
    console.warn("[DEBUG] ⚠ NO SE ENCONTRARON ITEMS PARA REMITO:", remito_id);
  }
  return items;
}

/* ====== FORMATEO ====== */
export function buildCodigoDigits(it) {
  const val =
    onlyDigits(it?.instr_os_code) ??
    onlyDigits(it?.os_code) ??
    (it?.instr_lab_id_text ?? null) ??
    (it?.instr_lab_id ?? null) ??
    (it?.tag ?? "");
  return (val ?? "").toString();
}

function spanishType(type) {
  if (!type) return null;
  const t = String(type).toLowerCase();
  if (t.includes("pressure") || t.includes("presion") || t.includes("presión"))
    return "MÓDULO DE PRESIÓN";
  if (t.includes("temperature") || t.includes("temperatura"))
    return "TERMÓMETRO / TEMPERATURA";
  if (t.includes("dimensional")) return "DIMENSIONAL";
  if (t.includes("multimeter")) return "MULTÍMETRO";
  if (t.includes("manometer")) return "MANÓMETRO";
  if (t.includes("hygro") || t.includes("humidity") || t.includes("humedad"))
    return "HIGRÓMETRO / HUMEDAD";
  return String(type).toUpperCase();
}

function buildDescripcionES(it) {
  const partes = [];

  const baker = it.baker_code || it.instr_baker_code;
  if (baker) partes.push(`PO ${baker}`);

  // ✅ PRIORIDAD: Categoría humana (instrument_categories.name)
  const categoria = it?.instr_category_name ? String(it.instr_category_name).trim() : null;

  // Fallback: tipo viejo
  const tipoES = spanishType(it.instr_type);

  if (categoria) partes.push(categoria);
  else if (tipoES) partes.push(tipoES);

  if (it.instr_brand)
    partes.push(`MARCA: ${String(it.instr_brand).toUpperCase()}`);
  if (it.instr_model) partes.push(`MOD: ${it.instr_model}`);
  if (it.instr_serial) partes.push(`S/N: ${it.instr_serial}`);
  if (it.tag) partes.push(`TAG: ${it.tag}`);

  if (!categoria && !tipoES && it.instr_label) return it.instr_label;
  return partes.filter(Boolean).join(", ");
}

/** Separa dirección en calle / localidad / provincia desde un solo campo */
function parseAddressPartsFromSingleField(address) {
  const raw = (address || "").trim();
  let street = "",
    locality = "",
    province = "";

  if (raw) {
    const norm = raw.replace(/\s*–\s*/g, "-").replace(/\s*—\s*/g, "-");
    const [first, ...restParts] = norm
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (first) street = first;

    const rest = restParts.join(", ");
    if (rest) {
      const byDash = rest
        .split("-")
        .map((s) => s.trim())
        .filter(Boolean);
      const byComma = rest
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      if (byDash.length >= 2) {
        locality = byDash[0];
        province = byDash[1];
      } else if (byComma.length >= 2) {
        locality = byComma[0];
        province = byComma[1];
      } else {
        locality = rest;
      }
    }
  }

  street = (street || "").replace(/\s+/g, " ").trim();
  locality = (locality || "").replace(/\s+/g, " ").trim();
  province = (province || "").replace(/\s+/g, " ").trim();

  return { street, locality, province };
}

/* ====== DIBUJO ====== */
function drawLabelValue(doc, label, value, x, y) {
  doc.setFont("helvetica", "bold");
  doc.text(`${label}:`, mm(x), mm(y));
  const labelW = doc.getTextWidth(`${label}:`);
  doc.setFont("helvetica", "normal");
  doc.text(`${value || ""}`, mm(x) + labelW + 2, mm(y));
}

function drawRemito(doc, head, client, items, opts = {}) {
  console.log("[DEBUG] ==== drawRemito called ====", {
    count: items?.length || 0,
  });

  const P = AFIP_CALINS;
  const offX = (opts.offsetX ?? 0) + P.offsetX;
  const offY = (opts.offsetY ?? 0) + P.offsetY;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(P.fontSize);

  // ✅ Fecha DD/MM/AAAA sin corrimiento
  const fecha = head?.date_out ? formatDateDDMMYYYY(head.date_out) : "";

  let nroRemito = "";
  if (head.official_number) {
    nroRemito = String(head.official_number).trim();
    const m = nroRemito.match(/^\d{4}-(.+)$/); // ej: "0001-00002396" -> "00002396"
    if (m) nroRemito = m[1].trim();
  }

  if (nroRemito) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(P.fontSizeNumber || P.fontSize + 2);
    doc.text(nroRemito, P.numberCopy.x + offX, P.numberCopy.y + offY);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(P.fontSize);
  }

  if (fecha) {
    doc.text(fecha, P.dateMain.x + offX, P.dateMain.y + offY);
  }

  // Centro de costo (desde items[0].cost_center o derivado)
  let costCenter =
    items?.[0]?.cost_center ?? extractCostCenterFromOSCode(items?.[0]?.os_code);
  if (!costCenter && items?.[0]?.os_base) {
    costCenter = String(items[0].os_base).padStart(4, "0").slice(-4);
  }

  // Cliente
  const cuitVal =
    (client.cuit && String(client.cuit)) ||
    (client.tax_id && String(client.tax_id)) ||
    (items?.[0]?.client_cuit && String(items[0].client_cuit)) ||
    "";

  const parsedAddr = parseAddressPartsFromSingleField(client?.address);

  // ✅ Dirección separada (clients.address / clients.locality / clients.province)
  // Fallback: si client.locality/province están vacíos, intentamos extraer desde address.
  const street = (client?.address ?? parsedAddr.street ?? "").trim();
  const locality = (client?.locality ?? parsedAddr.locality ?? "").trim();
  const province = (client?.province ?? parsedAddr.province ?? "").trim();

  drawLabelValue(
    doc,
    "Cliente",
    client?.name || "(sin nombre)",
    P.clientBlockX + offX,
    P.clientNameY + offY
  );

  const dirText = street || "";
  drawLabelValue(
    doc,
    "Dirección",
    dirText,
    P.clientBlockX + offX,
    P.clientAddrY + offY
  );

  const locText = locality || "";
  drawLabelValue(
    doc,
    "Localidad",
    locText,
    P.clientBlockX + offX,
    P.clientLocalY + offY
  );

  const provText = province || "";
  drawLabelValue(
    doc,
    "Provincia",
    provText,
    P.clientBlockX + offX,
    P.clientProvY + offY
  );

  drawLabelValue(
    doc,
    "C.U.I.T",
    cuitVal || "",
    P.clientBlockX + offX,
    P.clientCUITY + offY
  );

  // "Cliente Nº"
  doc.setFont("helvetica", "bold");
  doc.text("Cliente Nº:", P.clientNumber.x + offX, P.clientNumber.y + offY);
  doc.setFont("helvetica", "normal");
  doc.text(
    String(client?.client_number ?? ""),
    P.clientNumber.x + 20 + offX,
    P.clientNumber.y + offY
  );

  // "C. Costos"
  if (costCenter) {
    doc.setFont("helvetica", "bold");
    doc.text("C. Costos:", P.costCenter.x + offX, P.costCenter.y + offY);
    doc.setFont("helvetica", "normal");
    doc.text(
      String(costCenter),
      P.costCenter.x + 22 + offX,
      P.costCenter.y + offY
    );
  }

  // Tabla
  let yTbl = P.tableHeader.y + offY;
  doc.setFont("helvetica", "bold");
  doc.text("Código", P.tableHeader.xCod + offX, yTbl);
  doc.text("Cant", P.tableHeader.xQty + offX, yTbl);
  doc.text("Descripción", P.tableHeader.xDesc + offX, yTbl);
  doc.setFont("helvetica", "normal");
  yTbl += P.lineH;

  if (!items || items.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.text("(Sin items)", P.tableHeader.xDesc + offX, yTbl);
  } else {
    const sortedItems = [...items].sort((a, b) => {
      const numA = parseInt(
        onlyDigits(
          a?.instr_os_code ?? a?.os_code ?? a?.instr_lab_id ?? a?.tag ?? "0"
        ) || "0",
        10
      );
      const numB = parseInt(
        onlyDigits(
          b?.instr_os_code ?? b?.os_code ?? b?.instr_lab_id ?? b?.tag ?? "0"
        ) || "0",
        10
      );
      return numA - numB;
    });

    sortedItems.forEach((it) => {
      const cod =
        onlyDigits(it?.instr_os_code ?? it?.os_code ?? it?.instr_lab_id ?? it?.tag) ||
        "";
      const cant = String(it?.qty ?? 1);
      const desc = buildDescripcionES(it);

      doc.text(cod, P.tableHeader.xCod + offX, yTbl);
      doc.text(cant, P.tableHeader.xQty + offX, yTbl);

      const wrapped = doc.splitTextToSize(desc || "", 120);
      doc.text(wrapped, P.tableHeader.xDesc + offX, yTbl);
      yTbl += Math.max(P.lineH, (wrapped.length || 1) * (P.lineH - 1));
    });
  }

  // Pie
  doc.setFontSize(10);
  doc.text(
    "El cliente recibe en este acto el equipo según el ESTADO indicado en cada ítem, en el mismo estado y con todos los componentes con que fuera recibido.",
    20 + offX,
    P.footerY + offY,
    { maxWidth: 170 }
  );
}

/* ====== IMPRESIÓN ====== */
export async function printRemitoSoloTexto(remito_id, opts = {}) {
  console.log("[DEBUG] ==== printRemitoSoloTexto ====", remito_id);

  const { data: head, error: hErr } = await supabase
    .from("remitos")
    .select("id, date_out, client_id, official_number")
    .eq("id", remito_id)
    .single();
  if (hErr) throw hErr;

  const items = await listRemitoItems(remito_id);

  let client = items?.[0]
    ? {
        name: items[0].client_name ?? "",
        address: items[0].client_address ?? "",
        locality: items[0].client_locality ?? "",
        province: items[0].client_province ?? "",
        cuit: items[0].client_cuit ?? "",
        client_number: items[0].client_number ?? "",
      }
    : null;

  // ✅ Si el cliente viene desde la vista/tablas sin locality/province, los completamos desde clients
  if (head?.client_id && (!client || !client.locality || !client.province)) {
    const { data: cliFix, error: cliFixErr } = await supabase
      .from("clients")
      .select("id,name,address,locality,province,cuit,tax_id,client_number")
      .eq("id", head.client_id)
      .maybeSingle();
    if (!cliFixErr && cliFix) {
      client = {
        ...(client || {}),
        name: client?.name || cliFix.name || "(sin nombre)",
        address: client?.address || cliFix.address || "",
        locality: client?.locality || cliFix.locality || "",
        province: client?.province || cliFix.province || "",
        cuit: client?.cuit || cliFix.cuit || cliFix.tax_id || "",
        client_number: client?.client_number || cliFix.client_number || "",
      };
    }
  }

  if (!client || !client.name) {
    const { data: cli, error: cliErr } = await supabase
      .from("clients")
      .select("id,name,address,locality,province,cuit,tax_id,client_number")
      .eq("id", head.client_id)
      .maybeSingle();
    if (cliErr) console.warn("[DEBUG] No se pudo cargar cliente:", cliErr);

    client = {
      name: cli?.name || "(sin nombre)",
      address: cli?.address || "",
      locality: cli?.locality || "",
      province: cli?.province || "",
      cuit: cli?.cuit || cli?.tax_id || "",
      client_number: cli?.client_number || "",
    };
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  drawRemito(doc, head, client, items, opts);

  const safeName = (client.name || "cliente").replace(/[\\/:*?"<>|]/g, "_");
  doc.save(`Remito_${remito_id}_${safeName}.pdf`);
}

export async function generateRemitoPDF(remito_id, opts = {}) {
  console.log("[DEBUG] ==== generateRemitoPDF ====", remito_id);
  const { templateUrl = "/assets/remitos/REMITO-CALINS.jpg", scale = 1 } = opts;

  const { data: head } = await supabase
    .from("remitos")
    .select("id, date_out, client_id, official_number")
    .eq("id", remito_id)
    .single();

  const items = await listRemitoItems(remito_id);

  let client = items?.[0]
    ? {
        name: items[0].client_name ?? "",
        address: items[0].client_address ?? "",
        locality: items[0].client_locality ?? "",
        province: items[0].client_province ?? "",
        cuit: items[0].client_cuit ?? "",
        client_number: items[0].client_number ?? "",
      }
    : null;

  // ✅ Si el cliente viene desde la vista/tablas sin locality/province, los completamos desde clients
  if (head?.client_id && (!client || !client.locality || !client.province)) {
    const { data: cliFix, error: cliFixErr } = await supabase
      .from("clients")
      .select("id,name,address,locality,province,cuit,tax_id,client_number")
      .eq("id", head.client_id)
      .maybeSingle();
    if (!cliFixErr && cliFix) {
      client = {
        ...(client || {}),
        name: client?.name || cliFix.name || "(sin nombre)",
        address: client?.address || cliFix.address || "",
        locality: client?.locality || cliFix.locality || "",
        province: client?.province || cliFix.province || "",
        cuit: client?.cuit || cliFix.cuit || cliFix.tax_id || "",
        client_number: client?.client_number || cliFix.client_number || "",
      };
    }
  }

  if (!client || !client.name) {
    const { data: cli, error: cliErr } = await supabase
      .from("clients")
      .select("id,name,address,locality,province,cuit,tax_id,client_number")
      .eq("id", head.client_id)
      .maybeSingle();
    if (cliErr) console.warn("[DEBUG] No se pudo cargar cliente:", cliErr);

    client = {
      name: cli?.name || "(sin nombre)",
      address: cli?.address || "",
      locality: cli?.locality || "",
      province: cli?.province || "",
      cuit: cli?.cuit || cli?.tax_id || "",
      client_number: cli?.client_number || "",
    };
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });

  try {
    if (templateUrl) {
      const img = await fetch(templateUrl).then((r) => r.blob());
      const reader = new FileReader();
      const asDataUrl = await new Promise((res) => {
        reader.onload = () => res(reader.result);
        reader.readAsDataURL(img);
      });
      doc.addImage(asDataUrl, "JPEG", 0, 0, 210 * scale, 297 * scale);
    }
  } catch (e) {
    console.warn("[DEBUG] No se pudo cargar la plantilla:", e);
  }

  drawRemito(doc, head, client, items, opts);

  const safeName = (client.name || "cliente").replace(/[\\/:*?"<>|]/g, "_");
  doc.save(`Remito_${remito_id}_${safeName}.pdf`);
}

/* ====== OPCIONALES ====== */
export async function markInstrumentDelivered(instrument_id) {
  const { error } = await supabase.rpc("service_mark_instrument_delivered", {
    p_instrument_id: instrument_id,
  });
  if (error) throw error;
}

export async function listRealInstruments(os_id) {
  const { data, error } = await supabase.rpc("service_list_real_instruments", {
    p_os_id: os_id,
  });
  if (error) throw error;
  return data ?? [];
}

export async function setInstrumentPrice({
  os_id,
  instrument_id,
  price,
  currency = "USD BNA",
}) {
  const { error } = await supabase.rpc("service_set_price", {
    p_os_id: os_id,
    p_instrument_id: instrument_id,
    p_price: price,
    p_currency: currency,
  });
  if (error) throw error;
}

/* ====== HELPERS PARA OFFERS ====== */
export async function getServiceOrderByOffer(offerId) {
  if (!offerId) return null;
  const { data, error } = await supabase
    .from("service_orders")
    .select("id, code")
    .eq("offer_id", offerId)
    .maybeSingle();
  if (error) return null;
  return data || null;
}

export async function createSOFromOfferSafe(offerId) {
  if (!offerId) return null;

  const existing = await getServiceOrderByOffer(offerId);
  if (existing?.id) return existing;

  const { data, error, status } = await supabase.rpc("create_so_from_offer", {
    p_offer_id: offerId,
  });

  if (error) {
    const msg = (error.message || "").toLowerCase();
    if (
      status === 409 ||
      msg.includes("conflict") ||
      msg.includes("duplicate") ||
      msg.includes("exists")
    ) {
      const again = await getServiceOrderByOffer(offerId);
      if (again?.id) return again;
      return null;
    }
    throw error;
  }

  if (data) {
    const row = Array.isArray(data) ? data[0] : data;
    if (row?.id) return { id: row.id, code: row.code };
  }

  const after = await getServiceOrderByOffer(offerId);
  return after || null;
}
